import React, { useState } from "react";
import { BellRing, Check, CheckCheck, ChevronDown, ChevronUp, Loader2, MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { errMsg, fmtDateTime } from "../lib/api";
import { useDeletionAlerts } from "../lib/deletionAlerts";

const PREVIEW = 5;

/**
 * Phase 193 — "Unread alerts" strip at the top of Super Admin › Deleted Properties.
 * One card per alert: Municipal Admin name, municipality, PIN(s), reason, time; Mark as read per alert + Mark all read.
 * Clicking "Open bin" switches the page to that municipality (and pre-fills the first PIN in the search).
 */
export const DeletionAlertsStrip = ({ onOpen, testId = "deletion-alerts" }) => {
  const alerts = useDeletionAlerts({ state: "unread", limit: 50 });
  const [busy, setBusy] = useState(null);     // alert id | "all"
  const [expanded, setExpanded] = useState(false);
  const [openPins, setOpenPins] = useState({});

  if (alerts.loading && !alerts.data) return null;
  if (alerts.error && !alerts.data) return null;
  if (!alerts.items.length) return null;

  const read = async (id) => {
    setBusy(id);
    try { await alerts.markRead(id); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const readAll = async () => {
    setBusy("all");
    try {
      const r = await alerts.markAllRead();
      toast.success(`${r.marked} alert${r.marked === 1 ? "" : "s"} marked as read`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const shown = expanded ? alerts.items : alerts.items.slice(0, PREVIEW);

  return (
    <Card className="shadow-sm border-rose-200 bg-rose-50/40 dark:bg-rose-950/10" data-testid={`${testId}-strip`}>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-700"><BellRing className="h-4 w-4" /></span>
          <div className="mr-2">
            <p className="text-sm font-medium flex items-center gap-2">
              Unread deletion alerts
              <Badge className="bg-rose-600 hover:bg-rose-600 text-white tabular-nums" data-testid={`${testId}-unread-count`}>{alerts.unread}</Badge>
            </p>
            <p className="text-[11px] text-muted-foreground">Properties deleted by Municipal Admins — who, where, which PINs and why. Marking as read only hides the alert; the records stay in the recycle bin.</p>
          </div>
          <Button size="sm" variant="outline" className="h-8 ml-auto" onClick={readAll} disabled={!!busy} data-testid={`${testId}-mark-all-read`}>
            {busy === "all" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5 mr-1.5" />} Mark all as read
          </Button>
        </div>

        <TooltipProvider delayDuration={200}>
          <ul className="space-y-2" data-testid={`${testId}-list`}>
            {shown.map((a) => {
              const pins = a.pins || [];
              const showAll = openPins[a.id];
              const visible = showAll ? pins : pins.slice(0, 6);
              return (
                <li key={a.id} className="rounded-lg border bg-background px-3 py-2.5 flex flex-col sm:flex-row sm:items-start gap-2" data-testid={`${testId}-item-${a.id}`} data-kind={a.kind}>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                      <Trash2 className="h-3.5 w-3.5 text-rose-600 shrink-0" />
                      <span className="font-medium" data-testid={`${testId}-admin-${a.id}`}>{a.deleted_by_name || "Municipal Admin"}</span>
                      <span className="text-muted-foreground">deleted</span>
                      <Badge variant="outline" className="text-[10px] tabular-nums" data-testid={`${testId}-count-${a.id}`}>{a.count} propert{a.count === 1 ? "y" : "ies"}{a.kind === "bulk" ? " · bulk" : ""}</Badge>
                      <span className="text-muted-foreground">in</span>
                      <span className="flex items-center gap-1 font-medium" data-testid={`${testId}-muni-${a.id}`}><MapPin className="h-3 w-3 text-muted-foreground" />{a.municipality_name || a.municipality_code}</span>
                      <span className="text-xs text-muted-foreground tabular-nums" data-testid={`${testId}-time-${a.id}`}>· {fmtDateTime(a.created_at)}</span>
                      {a.blocked > 0 && <span className="text-[11px] text-[hsl(var(--warning))]">({a.blocked} skipped — receipts exist)</span>}
                    </div>
                    <p className="text-xs" data-testid={`${testId}-pins-${a.id}`}>
                      <span className="text-muted-foreground">PIN{pins.length === 1 ? "" : "s"}: </span>
                      <span className="font-mono break-words">{visible.join(", ")}</span>
                      {pins.length > 6 && (
                        <button type="button" className="ml-1.5 text-primary underline-offset-2 hover:underline" onClick={() => setOpenPins((s) => ({ ...s, [a.id]: !showAll }))} data-testid={`${testId}-pins-toggle-${a.id}`}>
                          {showAll ? "show fewer" : `+${pins.length - 6} more`}
                        </button>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`${testId}-reason-${a.id}`}><span className="font-medium text-foreground">Reason:</span> {a.reason || "—"}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 sm:pt-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="outline" className="h-8" onClick={() => onOpen?.(a)} data-testid={`${testId}-open-${a.id}`}>
                          <MapPin className="h-3.5 w-3.5 mr-1" /> Open bin
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">Show this municipality's recycle bin, searched for the first PIN</TooltipContent>
                    </Tooltip>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => read(a.id)} disabled={!!busy} data-testid={`${testId}-mark-read-${a.id}`}>
                      {busy === a.id ? <Loader2 className="h-3.5 w-3.5 mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />} Mark as read
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </TooltipProvider>

        {alerts.items.length > PREVIEW && (
          <Button variant="ghost" size="sm" className="h-8 w-full" onClick={() => setExpanded((v) => !v)} data-testid={`${testId}-toggle-more`}>
            {expanded ? <><ChevronUp className="h-3.5 w-3.5 mr-1" /> Show fewer</> : <><ChevronDown className="h-3.5 w-3.5 mr-1" /> Show all {alerts.items.length} unread alerts</>}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default DeletionAlertsStrip;
